/**
 * Request deduplication utility
 * Prevents duplicate API calls for the same request
 */

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

const pendingRequests = new Map<string, PendingRequest>();
const REQUEST_TIMEOUT = 30000; // 30 seconds

/**
 * Deduplicate requests - if a request with the same key is already pending, return the existing promise
 */
export function deduplicateRequest<T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> {
  // Check if there's a pending request with the same key
  const existing = pendingRequests.get(key);
  
  if (existing) {
    // Check if request is still valid (not expired)
    const age = Date.now() - existing.timestamp;
    if (age < REQUEST_TIMEOUT) {
      return existing.promise;
    } else {
      // Request expired, remove it
      pendingRequests.delete(key);
    }
  }

  // Create new request
  const promise = requestFn()
    .then((result) => {
      // Remove from pending after completion
      pendingRequests.delete(key);
      return result;
    })
    .catch((error) => {
      // Remove from pending on error
      pendingRequests.delete(key);
      throw error;
    });

  // Store pending request
  pendingRequests.set(key, {
    promise,
    timestamp: Date.now(),
  });

  return promise;
}

/**
 * Clear all pending requests (useful for cleanup or testing)
 */
export function clearPendingRequests(): void {
  pendingRequests.clear();
}

/**
 * Generate a request key from parameters
 */
export function generateRequestKey(prefix: string, ...params: any[]): string {
  return `${prefix}:${JSON.stringify(params)}`;
}

